import { useCallback, useEffect, useRef, useState } from "react";

// Drag-to-resize column widths, remembered per browser.
//
// A list like this gets read differently by different people: one wants the
// whole asunto, the next wants ten rows of radicados on screen at once. Rather
// than guess a single layout, let them drag the edge and keep what they chose —
// the widths live in localStorage, so the choice survives a reload without
// costing a round trip or a user-preferences table.

const MIN_WIDTH = 60;

const read = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return {};
    // Trust nothing that comes back out of storage: a stale or hand-edited
    // entry must not be able to render a column 4px wide or NaN.
    return Object.fromEntries(
      Object.entries(parsed)
        .filter(([, v]) => Number.isFinite(v) && v >= MIN_WIDTH)
        .map(([k, v]) => [k, Math.round(v)])
    );
  } catch {
    // Private windows and blocked site data both throw here.
    return {};
  }
};

const write = (key, widths) => {
  try {
    if (Object.keys(widths).length) window.localStorage.setItem(key, JSON.stringify(widths));
    else window.localStorage.removeItem(key);
  } catch {
    // Not being able to remember the width is not worth breaking the page over.
  }
};

export default function useColumnWidths(storageKey) {
  const [widths, setWidths] = useState(() => read(storageKey));
  // Live drag state, kept in a ref so a pointermove does not re-render twice.
  const drag = useRef(null);
  const [dragging, setDragging] = useState(false);

  const startResize = useCallback((columnKey, event) => {
    const cell = event.currentTarget.closest("th");
    if (!cell) return;
    event.preventDefault();
    event.stopPropagation();
    drag.current = {
      columnKey,
      startX: event.clientX,
      startWidth: cell.getBoundingClientRect().width,
    };
    // The first drag takes the table off content-based sizing, which would
    // otherwise hand every untouched column an identical share and truncate
    // radicados nobody touched. So freeze what the browser had worked out:
    // from here the dragged column moves and the rest stay where they were.
    setWidths((prev) => {
      if (Object.keys(prev).length) return prev;
      const row = cell.closest("tr");
      if (!row) return prev;
      const seeded = {};
      for (const th of row.querySelectorAll("th[data-column]")) {
        const width = th.getBoundingClientRect().width;
        // Columns hidden at this width measure 0 and must stay unset, so they
        // size themselves when a wider screen brings them back.
        if (width >= MIN_WIDTH) seeded[th.dataset.column] = Math.round(width);
      }
      return Object.keys(seeded).length ? seeded : prev;
    });
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => {
      const d = drag.current;
      if (!d) return;
      const next = Math.max(MIN_WIDTH, Math.round(d.startWidth + (e.clientX - d.startX)));
      setWidths((prev) => (prev[d.columnKey] === next ? prev : { ...prev, [d.columnKey]: next }));
    };
    const end = () => {
      drag.current = null;
      setDragging(false);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    // While dragging, the pointer is over text: without this the browser starts
    // selecting the table instead of resizing it.
    const previousSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      document.body.style.userSelect = previousSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [dragging]);

  // Persist once the drag ends rather than on every pixel.
  useEffect(() => {
    if (dragging) return;
    write(storageKey, widths);
  }, [dragging, widths, storageKey]);

  const resetColumn = useCallback((columnKey) => {
    setWidths((prev) => {
      if (!(columnKey in prev)) return prev;
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  }, []);

  const resetAll = useCallback(() => setWidths({}), []);

  // A width on one column only means anything if the table stops sizing itself.
  const hasWidths = Object.keys(widths).length > 0;

  return { widths, startResize, resetColumn, resetAll, dragging, hasWidths };
}
